<template>
    <VfModal id="m-auth-enrollFingerprint" ref="modal">
        <template #header>
            Enroll {{ fingerName }} Fingerprint
            <template v-if="account"> for {{ account.firstName }} {{ account.lastName }} </template>
        </template>

        <p>
            Please press your {{ fingerName.toLowerCase() }} finger down on the reader for 1 second then release.<br />Repeat this process until you
            have collected 5 samples.
        </p>

        <div class="fingerprint-samples">
            <div v-for="(sample, i) in samples" :key="i">
                <i v-if="!sample" class="fa fa-fingerprint"></i>
                <img v-else :src="`data:image/png;base64,${sample.Png}`" />
            </div>
        </div>

        <Loader v-if="isSubmitting" size="2xl" />

        <template #footer>
            <button type="button" @click="callback()">Cancel</button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { VfModal, vfModalRef } from '@signal24/vue-foundation';
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { DesktopBridge, type IInboundEventPayloads } from '@/modules/desktop/helpers/bridge';
import { type IAccount, type IAccountFingerprint, IdentityApi } from '@/openapi-clients-generated/auth';
import Loader from '@/shared/components/loader.vue';
import { withWait } from '@/shared/helpers/request.helpers';
import { dataFrom } from '@signal24/openapi-client-codegen';

const props = defineProps<{
    account?: IAccount;
    fingerName: string;
    fingerIdx: number;
    callback: (fingerprint?: IAccountFingerprint) => void;
}>();

const modal = vfModalRef();

type IFingerprintSample = IInboundEventPayloads['fingerprint'] | null;
const samples = ref<IFingerprintSample[]>([null, null, null, null, null]);
const isSubmitting = ref(false);

function handleFingerprint(data: IInboundEventPayloads['fingerprint']) {
    const targetIdx = samples.value.findIndex(s => s === null);
    if (targetIdx === -1) return;
    samples.value[targetIdx] = data;
    if (targetIdx === samples.value.length - 1) {
        performEnroll();
    }
}

async function performEnroll() {
    isSubmitting.value = true;

    const fingerprint = await withWait('Enrolling fingerprint...', async () =>
        dataFrom(
            await IdentityApi.postIdentityEnrollFingerprint({
                body: {
                    accountId: props.account?.id,
                    finger: props.fingerIdx,
                    fmds: samples.value.map(s => s!.Data)
                }
            })
        )
    );

    props.callback(fingerprint);
}

onMounted(async () => {
    DesktopBridge.addEventListener('fingerprint', handleFingerprint);
});

onBeforeUnmount(async () => {
    DesktopBridge.removeEventListener('fingerprint', handleFingerprint);
});
</script>

<style scoped>
@reference '#tw.css';

p {
    @apply text-center;
}

.fingerprint-samples {
    @apply mt-4 flex gap-2 justify-center;

    div {
        @apply w-32 h-32 bg-gray-100 rounded-md flex items-center justify-center;

        i {
            @apply text-6xl text-gray-200;
        }
    }
}
</style>
